import { useState, useEffect } from 'react'
import { useSessionStore } from '../store/sessionStore'
import Tooltip from './Tooltip'
import type { AgentConfig } from '../types'
import './ForecastSettings.css'

export interface ForecastConfig {
  formatter_model: string
  predictor_model: string
  supervisor_model: string
  search_depth: 'low' | 'medium' | 'high'
  predictor_reasoning_effort: 'minimal' | 'low' | 'medium' | 'high'
  supervisor_reasoning_effort: 'minimal' | 'low' | 'medium' | 'high'
  num_agents: number
  agent_configs?: AgentConfig[] | null
}

interface ForecastSettingsProps {
  config: ForecastConfig
  onChange: (config: ForecastConfig) => void
}

const MODELS = [
  { id: 'gpt-5-nano', name: 'GPT-5 Nano' },
  { id: 'gpt-5-mini', name: 'GPT-5 Mini' },
  { id: 'gpt-5', name: 'GPT-5' },
  { id: 'gpt-5.1', name: 'GPT-5.1' },
]

const SETTING_TOOLTIPS = {
  formatter_model: 'The AI model used to reformat and validate your question into a well-specified forecasting format.',
  predictor_model: 'The AI model used by prediction agents to make individual forecasts.',
  supervisor_model: 'The AI model used to synthesise and aggregate predictions from multiple agents.',
  num_agents: 'The number of independent prediction agents that will make forecasts. More agents provide better consensus but increase cost and time.',
  search_depth: 'Controls how extensively agents search the web for information. Higher depth = more thorough research but slower and more expensive.',
  predictor_reasoning_effort: 'Controls how much reasoning the prediction agents perform. Higher effort = more detailed agent analysis but slower and more expensive.',
  supervisor_reasoning_effort: 'Controls how much reasoning the supervisor performs when synthesizing agent predictions. Higher effort = more thorough synthesis and better scenario generation.',
}

const INSTRUCTION_PRESETS = {
  supporting: {
    name: 'Supporting',
    instructions:
      "Actively seek out evidence and arguments that support the resolution of this question (i.e. outcome is 'yes'). Look for reasons why the event is more likely to occur than people might think.",
  },
  opposing: {
    name: 'Opposing',
    instructions:
      "Actively seek out evidence and arguments against the resolution of this question (i.e. outcome is 'no'). Look for reasons why the event is less likely to occur than people might think.",
  },
  prediction_markets: {
    name: 'Prediction markets',
    instructions:
      'You seek out information about this prediction on any available prediction markets (if related prediction markets exist). You search on Metaculus, Polymarket, Kalshi and Oddschecker. You believe in prediction markets and lean heavily on the predictions in these markets as part of your analysis.',
  },
}

export default function ForecastSettings({ config, onChange }: ForecastSettingsProps) {
  const hasModelAccess = useSessionStore((state) => state.hasModelAccess)
  const isAdmin = useSessionStore((state) => state.isAdmin)
  const [customInstructionsOpen, setCustomInstructionsOpen] = useState(false)
  const [applyToAll, setApplyToAll] = useState(false)
  const [gateOpen, setGateOpen] = useState(false)

  // Non-admin users are limited to nano for now (until BYO key is supported).
  const availableModels = MODELS.filter((m) => (isAdmin ? true : m.id === 'gpt-5-nano')).filter((m) => hasModelAccess(m.id))

  const showGate = () => setGateOpen(true)

  const guardedUpdate = (updates: Partial<ForecastConfig>) => {
    if (!isAdmin) {
      const modelKeys: Array<keyof ForecastConfig> = ['formatter_model', 'predictor_model', 'supervisor_model']
      for (const k of modelKeys) {
        const v = (updates as any)[k]
        if (typeof v === 'string' && v !== 'gpt-5-nano') {
          showGate()
          return
        }
      }
      if (updates.search_depth && updates.search_depth !== 'low') {
        showGate()
        return
      }
      if (updates.predictor_reasoning_effort && updates.predictor_reasoning_effort !== 'low') {
        showGate()
        return
      }
      if (updates.supervisor_reasoning_effort && updates.supervisor_reasoning_effort !== 'medium') {
        showGate()
        return
      }
      if (typeof updates.num_agents === 'number' && updates.num_agents > 10) {
        showGate()
        return
      }
    }
    onChange({ ...config, ...updates })
  }

  const updateConfig = (updates: Partial<ForecastConfig>) => {
    guardedUpdate(updates)
  }

  // Initialize agent_configs when num_agents changes
  useEffect(() => {
    const currentConfigs = config.agent_configs || []
    if (currentConfigs.length !== config.num_agents) {
      const newConfigs: AgentConfig[] = []
      for (let i = 0; i < config.num_agents; i++) {
        newConfigs.push(currentConfigs[i] || { name: '', instructions: '' })
      }
      updateConfig({ agent_configs: newConfigs })
    }
  }, [config.num_agents]) // eslint-disable-line react-hooks/exhaustive-deps

  const getAgentConfigs = (): AgentConfig[] => {
    if (applyToAll) {
      const allInstructions = (config.agent_configs?.[0]?.instructions || '').trim()
      return Array(config.num_agents).fill(null).map(() => ({
        name: '',
        instructions: allInstructions,
      }))
    }
    return config.agent_configs || Array(config.num_agents).fill(null).map(() => ({ name: '', instructions: '' }))
  }

  const updateAgentConfig = (index: number, updates: Partial<AgentConfig>) => {
    const configs = getAgentConfigs()
    configs[index] = { ...configs[index], ...updates }
    
    // If applyToAll, update all configs
    if (applyToAll) {
      for (let i = 0; i < configs.length; i++) {
        configs[i] = { ...configs[i], ...updates }
      }
    }
    
    // Only include non-empty configs
    const hasCustomisation = configs.some(c => c.name?.trim() || c.instructions?.trim())
    updateConfig({ agent_configs: hasCustomisation ? configs : null })
  }

  const applyPreset = (presetType: keyof typeof INSTRUCTION_PRESETS, index?: number) => {
    const preset = INSTRUCTION_PRESETS[presetType]
    const updates: Partial<AgentConfig> = { name: preset.name, instructions: preset.instructions }
    if (applyToAll || index === undefined) {
      updateAgentConfig(0, updates)
    } else {
      updateAgentConfig(index, updates)
    }
  }

  const clearAllInstructions = () => {
    updateConfig({ agent_configs: null })
  }

  const agentConfigs = getAgentConfigs()

  return (
    <div className="forecast-settings">
      <div className="settings-grid">
        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.formatter_model}>
            <label htmlFor="formatter-model">
              Formatter Model
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <select
            id="formatter-model"
            value={config.formatter_model}
            onChange={(e) => updateConfig({ formatter_model: e.target.value })}
          >
            {availableModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.predictor_model}>
            <label htmlFor="predictor-model">
              Predictor Model
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <select
            id="predictor-model"
            value={config.predictor_model}
            onChange={(e) => updateConfig({ predictor_model: e.target.value })}
          >
            {availableModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.supervisor_model}>
            <label htmlFor="supervisor-model">
              Supervisor Model
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <select
            id="supervisor-model"
            value={config.supervisor_model}
            onChange={(e) => updateConfig({ supervisor_model: e.target.value })}
          >
            {availableModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.num_agents}>
            <label htmlFor="num-agents">
              Number of Agents
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <input
            id="num-agents"
            type="number"
            min="1"
            max="10"
            value={config.num_agents}
            onChange={(e) => updateConfig({ num_agents: parseInt(e.target.value) || 10 })}
          />
        </div>

        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.search_depth}>
            <label htmlFor="search-depth">
              Search Depth
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <select
            id="search-depth"
            value={config.search_depth}
            onChange={(e) =>
              updateConfig({ search_depth: e.target.value as ForecastConfig['search_depth'] })
            }
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.predictor_reasoning_effort}>
            <label htmlFor="predictor-reasoning-effort">
              Predictor Reasoning
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <select
            id="predictor-reasoning-effort"
            value={config.predictor_reasoning_effort}
            onChange={(e) =>
              updateConfig({
                predictor_reasoning_effort: e.target.value as ForecastConfig['predictor_reasoning_effort'],
              })
            }
          >
            <option value="minimal">Minimal</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div className="setting-group">
          <Tooltip content={SETTING_TOOLTIPS.supervisor_reasoning_effort}>
            <label htmlFor="supervisor-reasoning-effort">
              Supervisor Reasoning
              <span className="tooltip-icon">ℹ</span>
            </label>
          </Tooltip>
          <select
            id="supervisor-reasoning-effort"
            value={config.supervisor_reasoning_effort}
            onChange={(e) =>
              updateConfig({
                supervisor_reasoning_effort: e.target.value as ForecastConfig['supervisor_reasoning_effort'],
              })
            }
          >
            <option value="minimal">Minimal</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div className="custom-instructions-section">
        <button
          type="button"
          className="custom-instructions-header"
          onClick={() => setCustomInstructionsOpen(!customInstructionsOpen)}
        >
          <div className="custom-instructions-title">
            <span>Custom Agent Instructions</span>
            <Tooltip content="Custom instructions allow you to steer individual agents towards specific research angles or perspectives. Examples: 'Prioritise academic sources', 'Take a contrarian perspective', 'Focus on economic indicators'.">
              <span className="tooltip-icon">ℹ</span>
            </Tooltip>
          </div>
          <span className="collapse-icon">{customInstructionsOpen ? '▾' : '▸'}</span>
        </button>

        {customInstructionsOpen && (
          <div className="custom-instructions-content">
            <div className="preset-buttons">
              <button
                type="button"
                className="preset-btn"
                onClick={clearAllInstructions}
              >
                ✕ Clear All Instructions
              </button>
            </div>

            <label className="apply-all-toggle">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
              />
              Apply same instructions to all agents
            </label>

            <div className="agent-configs-container">
              {applyToAll ? (
                <div className="agent-config-card single-mode">
                  <div className="agent-config-header">
                    <span>Instructions for all {config.num_agents} agents</span>
                  </div>
                  <div className="agent-preset-buttons">
                    <button
                      type="button"
                      className="agent-preset-btn"
                      onClick={() => applyPreset('supporting')}
                    >
                      📈 Supporting
                    </button>
                    <button
                      type="button"
                      className="agent-preset-btn"
                      onClick={() => applyPreset('opposing')}
                    >
                      📉 Opposing
                    </button>
                    <button
                      type="button"
                      className="agent-preset-btn"
                      onClick={() => applyPreset('prediction_markets')}
                    >
                      🧾 Prediction markets
                    </button>
                    <button
                      type="button"
                      className="agent-preset-btn clear"
                      onClick={() => updateAgentConfig(0, { instructions: '' })}
                    >
                      ✕
                    </button>
                  </div>
                  <textarea
                    placeholder="Enter instructions that will apply to all agents..."
                    value={agentConfigs[0]?.instructions || ''}
                    onChange={(e) => updateAgentConfig(0, { instructions: e.target.value })}
                    rows={4}
                  />
                </div>
              ) : (
                agentConfigs.map((agentConfig, index) => (
                  <div key={index} className="agent-config-card">
                    <div className="agent-config-header">
                      <input
                        type="text"
                        placeholder={`Agent ${index + 1} name (optional)`}
                        value={agentConfig.name || ''}
                        onChange={(e) => updateAgentConfig(index, { name: e.target.value })}
                      />
                    </div>
                    <div className="agent-preset-buttons">
                      <button
                        type="button"
                        className="agent-preset-btn"
                        onClick={() => applyPreset('supporting', index)}
                      >
                        📈 Supporting
                      </button>
                      <button
                        type="button"
                        className="agent-preset-btn"
                        onClick={() => applyPreset('opposing', index)}
                      >
                        📉 Opposing
                      </button>
                      <button
                        type="button"
                        className="agent-preset-btn"
                        onClick={() => applyPreset('prediction_markets', index)}
                      >
                        🧾 Prediction markets
                      </button>
                      <button
                        type="button"
                        className="agent-preset-btn clear"
                        onClick={() => updateAgentConfig(index, { instructions: '' })}
                      >
                        ✕
                      </button>
                    </div>
                    <textarea
                      placeholder={`Enter custom instructions for Agent ${index + 1}...`}
                      value={agentConfig.instructions || ''}
                      onChange={(e) => updateAgentConfig(index, { instructions: e.target.value })}
                      rows={3}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {gateOpen && (
        <div className="modal-overlay" onClick={() => setGateOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-lg sm:text-xl font-semibold text-ink">Admin-only settings</h2>
              <button className="modal-close" type="button" onClick={() => setGateOpen(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="text-sm text-muted leading-relaxed">
                Only an admin can adjust higher-cost settings (models beyond Nano, deeper search/reasoning, or &gt;10 agents)
                because costs per prediction can increase considerably.
                <br />
                <br />
                I plan to add a “bring your own API key” feature so you can use deeper reasoning and better models at your
                own cost. Reach out via the Contact form if you want me to let you know when that’s added.
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2.5 border border-rule bg-paper text-ink font-semibold text-sm shadow-delphi hover:bg-white/40 transition"
                  onClick={() => setGateOpen(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

